// ** Third Party Components
import moment from "moment";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from "uuid";
import CryptoJS from "crypto-js";
import { Label, NavItem, NavLink, Card, CardBody } from "reactstrap";
import Compressor from "compressorjs";
import secureLocalStorage from "react-secure-storage";
import withReactContent from "sweetalert2-react-content";
import { HiOutlineArrowLeft } from "react-icons/hi";

// ** Variables
import { ApiRoutes } from "../@core/constants";
import { IsNotValue } from "../@core/constants/variables";

// ** Apis
import { getCategoryApi } from "../@core/api/templateCategoryApi";
import {
  languageListApi,
  templateCategoryList,
} from "../@core/api/templateApi";

// ** Redux & Actions
import { store } from "@store/store";
import {
  getAllCategoryListAction,
  getAllLanguagesAction,
  getTempCategoryListAction,
} from "../redux/actions/templateAction";

// ** Index DB
import { clearIndexedDB } from "../IndexDB/crudDB";
import {
  dbCategoryListAllData,
  dbGetAllCategoryData,
} from "../IndexDB/category";
import {
  dbGetAllLanguageData,
  dbLanguageListAllData,
} from "../IndexDB/language";
import {
  dbGetAllTempCategoryData,
  dbTempCategoryListAllData,
} from "../IndexDB/tempCategory";

// ** Custom Functions
import { notification } from "../@core/constants/notification";
import { addPendingRequestAction } from "../redux/actions/commonAction";
import { getUserWabaDetailsApi } from "../@core/api/metaService";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

const MySwal = withReactContent(Swal);

// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = (obj) => Object.keys(obj).length === 0;

/**
 ** Return if user is logged in
 ** This is completely up to you and how you want to store the token in your frontend application
 *  ? e.g. If you are using cookies to store the application please update this function
 */
export const getPanel = () => JSON.parse(secureLocalStorage.getItem("panel"));
export const getUserWabaData = () => secureLocalStorage.getItem("userWabaData");
export const isUserLoggedIn = () =>
  JSON.parse(secureLocalStorage.getItem("userData"));
export const getCurrentWabaDetails = () =>
  secureLocalStorage.getItem("currentWabaData");
export const getCurrentDeviceNumber = () =>
  secureLocalStorage.getItem("currentWabaPhoneNo");
export const isMetaPackagePurchased = () =>
  JSON.parse(secureLocalStorage.getItem("metaPackageData"));
export const getUserWabaCounter = () =>
  secureLocalStorage.getItem("userWabaDataCount");

/**
 ** This function is used for demo purpose route navigation
 ** In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 ** Please note role field is just for showing purpose it's not used by anything in frontend
 ** We are checking role just for ease
 * ? NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */
export const getHomeRouteForLoggedInUser = (userRole) => {
  const panel = getPanel();
  if (userRole == "unverifiedUser") return "/verify-registration";
  if (userRole == "admin") return panel ? "/metaDashboard" : "/dashboard";
  if (userRole == "SuperAdmin") return "/admin-dashboard";
  if (userRole == "staff") return panel ? "/chat" : "/dashboard";
  else "/login";
};

// Store User Data In Local-Storage
export const handleLogin = async (data) => {
  try {
    await secureLocalStorage.setItem("userData", JSON.stringify(data.userData));
    await secureLocalStorage.setItem(
      "metaPackageData",
      JSON.stringify(data.packageData)
    );
    await secureLocalStorage.setItem(
      "auth_token",
      JSON.stringify(data.userData.auth_token)
    );
  } catch (e) {}
};

// Remove Store User Data from Local-Storage
export const handleLogout = async () => {
  try {
    secureLocalStorage.clear("panel");
    secureLocalStorage.clear("userData");
    secureLocalStorage.clear("auth_token");
    secureLocalStorage.clear("userWabaData");
    secureLocalStorage.clear("currentWabaData");
    secureLocalStorage.clear("metaPackageData");
    secureLocalStorage.clear("userWabaDataCount");
    secureLocalStorage.clear("currentWabaPhoneNo");
    await clearIndexedDB();
  } catch (error) {}
};

export const addCommas = (number) =>
  number?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// Required Field Highlight
export const showRequired = <span className="text-danger"> *</span>;

// Show Toaster
export const showToaster = (status, msg) => {
  notification({
    type: status == 0 ? "error" : "success",
    title:
      msg == undefined ? "Something Went Wrong Please Try Again Later" : msg,
  });
};

// Remove code from field
export const removeCode = (str) => {
  const underscoreIndex = str.indexOf("_");
  if (underscoreIndex === -1) return str;
  return str.slice(underscoreIndex + 1);
};

// Delete Sweet Alert Function
export const deleteSweetAlert = ({
  title = "Are You Sure?",
  text = "",
  icon = "warning",
  confirmBtnText = "Delete",
  cancelBtnText = "Cancel",
  confirmBtn = "btn btn-danger",
  cancelBtn = "btn btn-outline-secondary ms-1",
}) => {
  return MySwal.fire({
    title: title,
    text: text,
    icon: icon,
    showCancelButton: true,
    confirmButtonText: confirmBtnText,
    cancelButtonText: cancelBtnText,
    customClass: {
      confirmButton: confirmBtn,
      cancelButton: cancelBtn,
    },
    buttonsStyling: false,
  });
};

// Data convert into encrypt formate
export const encryptMyData = (data) => {
  return CryptoJS.AES.encrypt(
    JSON.stringify(data),
    "my-secret-key@12"
    // process.env.REACT_APP_CRYPTO_JS_KEY
  ).toString();
};

// encrypt data convert into Original Data
export const decryptMyData = (data) => {
  const bytes = CryptoJS.AES.decrypt(data, "my-secret-key@12");
  const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
  return JSON.parse(decryptedData);
};

// Compress Media
export const mediaCompressor = async (data) => {
  let response = await new Promise((resolve, reject) => {
    let compress_res = new Compressor(data, {
      quality: 0.7, // 0.6 can also be used, but its not recommended to go below.
      convertSize: 1000,
      success: async (compressedResult) => {
        compressedResult.newFileName = compressedResult.name;
        console.log(
          `compressedFile size ${compressedResult.size / 1024 / 1024} MB`
        ); // smaller than maxSizeMB
        resolve({ status: 0, data: compressedResult });
      },
    });
  });
  return response;
};

export const encryptSocketData = (data) => {
  const requestId = Date.now(); // Unique request ID
  sendMessageToWebsocket(
    JSON.stringify({ data: encryptMyData({ ...data, requestId: requestId }) })
  );

  // Save request in Redux
  store.dispatch(addPendingRequestAction({ ...data, requestId: requestId }));
};

export const sendMessageToWebsocket = (data) => {
  const webSocket = store.getState().commonReducer.webSocket;
  webSocket.send(data);
};

// ** Remove All Duplicate Chat Message
export const mergeRemoveDuplicateSortChat = (
  oldChat,
  newChat,
  newMessage = false
) => {
  let chatList = [];
  if (!(oldChat.length == 0 && newMessage)) {
    chatList = newMessage ? [...oldChat, ...newChat] : [...newChat, ...oldChat];
  }
  const uniqueChat = chatList.filter((value, index, self) => {
    return self.findIndex((obj) => obj.waba_m_id === value.waba_m_id) === index;
  });
  return uniqueChat;
};

// ** Convert String message to Object
export const convertMessageStringToObject = (messageList) => {
  const updatedMessageList = messageList.map((message) => {
    if (
      message?.wp_msg_type == "text" &&
      !IsNotValue.includes(message?.context)
    ) {
      return {
        ...message,
        context: findMessageType(message?.context),
      };
    } else if (message?.wp_msg_type == "image") {
      if (
        message?.wp_msg_type == "image" &&
        IsNotValue.includes(message?.context)
      ) {
        return {
          ...message,
          wp_msg_text: findMessageType(message?.wp_msg_text),
        };
      } else {
        return {
          ...message,
          wp_msg_text: findMessageType(message?.wp_msg_text),
          context: findMessageType(message?.context),
        };
      }
    } else if (message?.wp_msg_type == "sticker") {
      if (
        message?.wp_msg_type == "sticker" &&
        IsNotValue.includes(message?.context)
      ) {
        return {
          ...message,
          wp_msg_text: findMessageType(message?.wp_msg_text),
        };
      } else {
        return {
          ...message,
          wp_msg_text: findMessageType(message?.wp_msg_text),
          context: findMessageType(message?.context),
        };
      }
    } else if (message?.wp_msg_type == "document") {
      if (
        message?.wp_msg_type == "document" &&
        IsNotValue.includes(message?.context)
      ) {
        return {
          ...message,
          wp_msg_text: findMessageType(message?.wp_msg_text),
        };
      } else {
        return {
          ...message,
          wp_msg_text: findMessageType(message?.wp_msg_text),
          context: findMessageType(message?.context),
        };
      }
    } else if (message?.wp_msg_type == "video") {
      if (
        message?.wp_msg_type == "video" &&
        IsNotValue.includes(message?.context)
      ) {
        return {
          ...message,
          wp_msg_text: findMessageType(message?.wp_msg_text),
        };
      } else {
        return {
          ...message,
          wp_msg_text: findMessageType(message?.wp_msg_text),
          context: findMessageType(message?.context),
        };
      }
    } else if (message?.wp_msg_type == "audio") {
      if (
        message?.wp_msg_type == "audio" &&
        IsNotValue.includes(message?.context)
      ) {
        return {
          ...message,
          wp_msg_text: findMessageType(message?.wp_msg_text),
        };
      } else {
        return {
          ...message,
          wp_msg_text: findMessageType(message?.wp_msg_text),
          context: findMessageType(message?.context),
        };
      }
    } else if (message?.wp_msg_type == "template") {
      if (
        message?.wp_msg_type == "template" &&
        IsNotValue.includes(message?.context)
      ) {
        return {
          ...message,
          wp_msg_text: findMessageType(message?.wp_msg_text),
        };
      } else {
        return {
          ...message,
          wp_msg_text: findMessageType(message?.wp_msg_text),
          context: findMessageType(message?.context),
        };
      }
    } else if (message?.wp_msg_type == "button") {
      if (
        message?.wp_msg_type == "button" &&
        IsNotValue.includes(message?.context)
      ) {
        return {
          ...message,
          wp_msg_text: findMessageType(message?.wp_msg_text),
        };
      } else {
        return {
          ...message,
          wp_msg_text: findMessageType(message?.wp_msg_text),
          context: findMessageType(message?.context),
        };
      }
    } else if (message?.wp_msg_type == "reaction") {
      return {
        ...message,
        wp_msg_text: findMessageType(message?.wp_msg_text),
      };
    } else if (message?.wp_msg_type == "contacts") {
      return {
        ...message,
        wp_msg_text: findMessageType(message?.wp_msg_text),
      };
    } else if (message?.wp_msg_type == "interactive") {
      return {
        ...message,
        wp_msg_text: findMessageType(message?.wp_msg_text),
        context: findMessageType(message?.context),
      };
    } else if (message?.wp_msg_type == "order") {
      let total = 0;
      let currency = "INR";
      const data = findMessageType(message?.wp_msg_text);
      data?.product_items.map((product) => {
        currency = product.currency;
        total += product?.item_price * product?.quantity;
      });
      return {
        ...message,
        wp_msg_text: data,
        total_amount:
          currency == "INR" ? `₹${total.toFixed(2)}` : `$${total.toFixed(2)}`,
      };
    }
    return message;
  });

  const messages = chatDateLine(updatedMessageList);
  return messages;
};

// ** Create Chat Date Show
export const chatDateLine = (chat) => {
  const dateList = [];
  const updateMessageList = chat.map((message) => ({
    ...message,
    showDate: false,
  }));
  const newMessage = updateMessageList.map((message) => {
    const date = moment(message.created_at).format("DD-MM-YYYY");
    if (!dateList.includes(date)) {
      dateList.push(date);
      return { ...message, showDate: true };
    }
    return message;
  });

  return newMessage;
};

// ** Identify Messages Type
export const findMessageType = (message) => {
  return typeof message == "string" ? JSON.parse(message) : message;
};

export const utcToLocal = ({
  date,
  dateFormat = "DD-MM-YYYY HH:mm",
  convertFormat = "DD-MM-YYYY HH:mm",
}) => {
  return !IsNotValue.includes("+05:30")
    ? moment.utc(date, dateFormat).utcOffset("+05:30").format(convertFormat)
    : moment().format(convertFormat);
};

// ** Format time Duration
export const formatAudioDuration = (duration) => {
  const minutes = Math.floor(duration / 60);
  const seconds = Math.floor(duration % 60);
  const formattedSeconds = seconds < 10 ? "0" + seconds : seconds;
  return `${minutes}:${formattedSeconds}`;
};

export const replaceBodyText = (text) => {
  if (!IsNotValue.includes(text)) {
    const replacements = [
      { regex: /\*(.*?)\*/gs, replacement: "<b>$1</b>" }, // Bold tags
      { regex: /_(.*?)_/gs, replacement: "<i>$1</i>" }, // Italic tags
      { regex: /~(.*?)~/gs, replacement: "<s>$1</s>" }, // Strikethrough tags
    ];

    text = text.toString();
    replacements.forEach(function (replacement) {
      text = text?.replace(replacement.regex, replacement.replacement);
    });
  }
  return text;
};

// Message Data
export const commonMessageData = (selectedChat) => {
  const user = isUserLoggedIn();
  const userWabaData = getCurrentWabaDetails();
  const userWabaNumber = getCurrentDeviceNumber();

  return {
    sender_id: user?.u_id,
    receiver_id: selectedChat?.waba_c_id,
    store_id: user?.u_type == 1 ? user?.u_id : user?.parent_u_id,
    u_waba_id: userWabaData?.u_waba_id,
    waba_device_id: userWabaNumber?.waba_device_id,
  };
};

// Common Sweet Alert Function
export const commonSweetAlert = (data) => {
  return MySwal.fire({
    title: data.title,
    text: data.text,
    icon: data.icon,
    width: 750,
    showCancelButton: data.showCancelButton,
    confirmButtonText: data.confirmBtnText,
    cancelButtonText: data.cancelBtnText,
    customClass: {
      confirmButton: "btn btn-success",
      cancelButton: "btn btn-outline-secondary ms-1",
    },
    buttonsStyling: false,
    allowOutsideClick: false,
  });
};

// Render Chat Label in Popper
export const renderTags = (chatData) => {
  const customerLabelList = store.getState().commonReducer.customerLabelList;
  const LabelIds = changeDataTypeStringToArray(chatData.c_tags);
  if (!IsNotValue.includes(LabelIds) && LabelIds.length > 0) {
    return (
      <div className="col-md-12">
        {customerLabelList?.map((label, index) => {
          return (
            LabelIds?.includes(label.a_id) && (
              <Label
                className="text-white text-center me-50 py-25 px-75 rounded"
                pill
                key={index}
                style={{ backgroundColor: label.color_code }}
              >
                {label.name}
              </Label>
            )
          );
        })}
      </div>
    );
  } else {
    return <div>No Label</div>;
  }
};

// Change Chat List order using Pin Or Unpin
export const sortChatsByPin = (chatList) => {
  return Object.values(chatList)
    .sort((a, b) => {
      // First sort by pin_chat (true first)
      if (a.c_pin === b.c_pin) {
        // If both are pinned or both are not pinned, sort alphabetically by c_fname
        // return a.c_fname.localeCompare(b.c_fname);
      }
      return b.c_pin - a.c_pin; // true (1) should come before false (0)
    })
    .reduce((chatList, chat) => {
      chatList[`waba_${chat.waba_c_id}`] = chat;
      return chatList;
    }, {});
};

// Change Type Array of Object to Object of Object
export const convertDataType = (chatList) => {
  return Object.values(chatList).reduce((chatList, chat) => {
    chatList[`waba_${chat.waba_c_id}`] = chat;
    return chatList;
  }, {});
};

// Get Base64 File Url
export const getBase64FileUrl = (files, setState) => {
  new Promise((resolve) => {
    let baseURL = "";
    const reader = new FileReader();
    reader.readAsDataURL(files);
    reader.onload = () => {
      baseURL = reader.result;
      setState(baseURL);
      resolve(baseURL);
    };
  });
};

// Update Chat Notifications Mute Or Unmute
export const handleMuteContactChat = (chatData, muteStatus) => {
  const user = isUserLoggedIn();
  deleteSweetAlert({
    title: `${muteStatus ? "Mute" : "Unmute"} notifications`,
    text: muteStatus
      ? "No one else in this chat will see that you muted it."
      : "",
    confirmBtnText: muteStatus ? "Mute" : "Unmute",
  }).then((result) => {
    if (result.value) {
      encryptSocketData({
        chatType: true,
        channel: "UPDATE_CUSTOMER_MUTE",
        data: {
          sender_id: user?.u_type == 1 ? user?.u_id : user?.parent_u_id,
          waba_c_id: chatData?.waba_c_id,
          c_mute: muteStatus,
          u_id: user?.u_id,
          store_id: chatData?.store_id,
          createdBy: user?.name,
        },
      });
    }
  });
};

// Change Chat Status
export const handleResolveContactChat = (chatData, resolveStatus) => {
  const user = isUserLoggedIn();
  deleteSweetAlert({
    title: `${resolveStatus ? "Resolve" : "Unresolved"} this chat?`,
    confirmBtnText: resolveStatus ? "Resolve Chat" : "Unresolved Chat",
    confirmBtn: "btn btn-primary",
  }).then((result) => {
    if (result.value) {
      encryptSocketData({
        chatType: true,
        channel: "UPDATE_CUSTOMER_CHAT_STATUS",
        data: {
          sender_id: user?.u_type == 1 ? user?.u_id : user?.parent_u_id,
          waba_c_id: chatData?.waba_c_id,
          c_chat_status: resolveStatus,
          store_id: user?.store_id,
          u_id: user?.u_id,
          createdBy: user.name,
        },
      });
    }
  });
};

export const changeDataTypeStringToArray = (data) => {
  if (!data) return [];
  const myString = data.replace(/^,|,$/g, "");
  return IsNotValue.includes(data) ? [] : JSON.parse("[" + myString + "]");
};

// ** Generate Unique ID (bot flow node id)
// Format: {login_u_id}_{uuid}  ex: 69_98chfghsdgahdsagh
// u_id login user data (localstorage userData) mathi, uuid `uuid` package thi.
// Aa j id canvas node id + payload na node_id ane flow_data id banne ma jay
// (save time element.id thi map thay che).
export const getUniqueId = () => {
  let prefix = "";
  try {
    const userData = isUserLoggedIn();
    const u = userData || {};
    // login userData mathi u_id (badha shape cover: top-level / nested)
    const uid = u.u_id ?? u.user?.u_id ?? u.data?.u_id ?? u.userData?.u_id;
    if (uid !== undefined && uid !== null && String(uid) !== "") {
      prefix = `${uid}_`;
    }
  } catch (error) {}
  return `${prefix}${uuidv4().replace(/-/g, "")}`;
};

let __uniqueCounter = 0;

// ** Generate Unique ID For Button
// ms*1000 + counter => same-ms calls get distinct ids, safe integer (< 9e15).
export const getUniqueButtonId = () => {
  __uniqueCounter += 1;
  return moment().valueOf() * 1000 + (__uniqueCounter % 1000);
};

// ** Prepare Template Data
export const prepareTemplateData = (data, type) => {
  const templateData = { body: data.body, template_type: data.template_type };
  if (data.header) templateData["header"] = data.header;
  if (data.category) templateData["category"] = data.category;
  if (data.footer) templateData["footer"] = data.footer;
  if (data.headerType) templateData["headerType"] = data.headerType;
  if (data.baseImage) templateData["baseImage"] = data.baseImage;
  if (data.carouselCard) templateData["carouselCard"] = data.carouselCard;
  if (data.buttonArray) {
    if (type == "node") {
      templateData["buttonArray"] = data.buttonArray;
    } else {
      if (data.category == 3) {
        templateData["buttonArray"] = data.buttonArray.map((button) => ({
          type: "button",
          sub_type: "URL",
          index: 0,
          parameters: [{ type: "text", text: button.example }],
        }));
      } else templateData["buttonArray"] = data.buttonArray;
    }
  }
  if (data.template_id) templateData["template_id"] = data.template_id;
  if (data.template) templateData["template"] = data.template;

  return templateData;
};

export const chatBotNodeValidation = (data) =>
  IsNotValue.includes(data) || IsNotValue.includes(data.trim());

export const chatBotButtonValidation = (data) => {
  let error = false;
  for (let i = 0; i < data.length; i++) {
    if (chatBotNodeValidation(data[i]?.title)) error = true;
  }
  return error;
};

export const renderBody = (text) => {
  var replacements = [
    { regex: /\*(.*?)\*/gs, replacement: "<b>$1</b>" }, // Bold tags
    { regex: /_(.*?)_/gs, replacement: "<i>$1</i>" }, // Italic tags
    { regex: /~(.*?)~/gs, replacement: "<s>$1</s>" }, // Strikethrough tags
  ];

  replacements.forEach(function (replacement) {
    text = text.replace(replacement.regex, replacement.replacement);
  });
  return text;
};

// Set Customer Name
export const renderCustomerName = (data) => {
  return (
    data.c_fname + " " + (IsNotValue.includes(data.c_lname) ? "" : data.c_lname)
  );
};

// Last Message Time
export const renderLastMessageTime = (data) => {
  const date = moment(data?.last_message_time, "YYYY-MM-DD HH:mm:ss").isSame(
    moment(),
    "date"
  );
  const utcToLocalDate = utcToLocal({
    date: data?.last_message_time,
    dateFormat: "YYYY-MM-DD HH:mm:ss",
    convertFormat: date ? "LT" : "DD-MM-YYYY HH:mm:ss",
  });

  return IsNotValue.includes(data?.last_message_time)
    ? ""
    : moment(utcToLocalDate, date ? "LT" : "DD-MM-YYYY HH:mm:ss").fromNow();
};

// Phone number format
export const formatPhoneNumber = (phoneNumber) => {
  return phoneNumber.replace(/^(\+\d{1,3})(\d{5})(\d{5})$/, "$1 $2 $3");
};

// Valid date
export const validateDate = (value, type) => {
  if (!value) return "Date is required";

  const datePattern = /^\d{4}-\d{2}-\d{2}$/;
  if (!datePattern.test(value)) return "Date must be in yyyy-mm-dd format";

  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() + 1 !== month ||
    date.getDate() !== day
  ) {
    return "Invalid date";
  }

  return true;
};

// Valid time
export const validateTime = (value) => {
  if (!value) return "Time is required";

  const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
  if (!timePattern.test(value)) return "Time must be in HH:mm format";

  return true;
};

// Date Convert To Local Time-Zone to UTC Time-Zone
export const localToUtc = (currentDate) => {
  const year = currentDate.getUTCFullYear();
  const month = String(currentDate.getUTCMonth() + 1).padStart(2, "0");
  const date = String(currentDate.getUTCDate()).padStart(2, "0");
  const hours = String(currentDate.getUTCHours()).padStart(2, "0");
  const minutes = String(currentDate.getUTCMinutes()).padStart(2, "0");
  const newDate = `${year}-${month}-${date} ${hours}:${minutes}:00`;
  return newDate;
};

// Create Random Id
export const randomId = () =>
  Math.floor(Math.random() * (999999 - 100000 + 1)) + 100000;

export const createNewObject = (obj) => {
  const newRes = [];
  let varFlag1 = false;
  let varFlag2 = false;
  obj &&
    obj.map((item, i) => {
      if (item) {
        let newObj = {};
        let newItem = item.split(",");
        var number = /^\d*[1-9\s]\d*$/;
        var varCounter = 1;
        newItem.map((item, i) => {
          if (i === 0) {
            if (!number.test(item?.trim())) {
              newObj = { ...newObj, name: item };
            } else if (number.test(item?.trim())) {
              newObj = { ...newObj, number: item?.trim() };
              varFlag1 = true;
            }
          }
          if (i === 1) {
            if (number.test(item)) {
              newObj = { ...newObj, number: item.trim() };
              varFlag2 = true;
            }
          }
          if (
            (varFlag1 && i > 0 && varCounter < 6) ||
            (varFlag2 && i > 1 && varCounter < 6)
          ) {
            newObj = { ...newObj, [`var${varCounter}`]: item };
            varCounter = varCounter + 1;
          }
        });
        newRes.push(newObj);
      }
    });
  return newRes;
};

export const groupBy = (arr, property) => {
  return arr.reduce(function (memo, x) {
    if (!memo[x[property]]) memo[x[property]] = [];
    memo[x[property]].push(x);
    return memo;
  }, {});
};

// Fetch Category List From Index DB
export const fetchCategoryListFromDB = async () => {
  const categoryListData = await dbGetAllCategoryData();
  if (categoryListData == undefined || categoryListData?.length == 0)
    handleCategoryList();
  else if (categoryListData?.length > 0)
    store.dispatch(getAllCategoryListAction(categoryListData));
};

export const handleCategoryList = async () => {
  await getCategoryApi()
    .then((response) => {
      const allCategoryOption = {
        id: 0,
        name: "All Category",
        icons: "Menu",
      };
      store.dispatch(
        getAllCategoryListAction([allCategoryOption, ...response?.data])
      );
      dbCategoryListAllData([allCategoryOption, ...response?.data]);
    })
    .catch((error) => showToaster(0, error?.response?.data?.description));
};

// Fetch Language List From Index DB
export const fetchLanguageListFromDB = async () => {
  const languageListData = await dbGetAllLanguageData();
  if (languageListData?.length == 0) handleGetLanguages();
  else if (languageListData?.length > 0)
    store.dispatch(getAllLanguagesAction(languageListData));
};

// GET LANGUAGE DATA
export const handleGetLanguages = async () => {
  await languageListApi()
    .then((response) => {
      store.dispatch(getAllLanguagesAction(response.data));
      dbLanguageListAllData(response?.data);
    })
    .catch((error) => showToaster(0, error?.response?.data?.description));
};

// Fetch Template Category From Index DB
export const fetchTempCategoryListFromDB = async () => {
  const tempCategoryListData = await dbGetAllTempCategoryData();
  if (tempCategoryListData?.length == 0) handleGetCategory();
  else if (tempCategoryListData?.length > 0)
    store.dispatch(getTempCategoryListAction(tempCategoryListData));
};

// GET CATEGORY DATA
export const handleGetCategory = async () => {
  await templateCategoryList()
    .then((response) => {
      store.dispatch(getTempCategoryListAction(response?.data));
      dbTempCategoryListAllData(response?.data);
    })
    .catch((error) => showToaster(0, error?.response?.data?.description));
};

export const handleTemplateMessagePreviewData = (data) => {
  let copyCodeValue = "";
  const previewCarouselCard = [];
  const variables = JSON.parse(data?.variables);
  const currentWabaData = getCurrentWabaDetails();
  const components = JSON.parse(data?.components);
  const templateMessagePreviewData = { template_type: data?.template_type };

  const headerVariable = variables.filter((item) => item.type == "Header");
  const bodyVariable = variables.filter((item) => item.type == "Body");
  const carouselVariable = variables.filter((item) => item.type == "Carousel");

  if (data?.category == 3) {
    const bodyComponent = components.find(
      (component) => component?.type == "BODY"
    );
    copyCodeValue = bodyComponent?.example.body_text[0];
  }

  components.map((item) => {
    if (item.type === "HEADER") {
      if (item.format == "TEXT") {
        let headerText = item.text;
        headerVariable.map((variable) => {
          if (variable.variable !== "") {
            headerText = headerText?.replace(variable.no, variable.variable);
          }
        });
        templateMessagePreviewData["header"] = headerText;
        templateMessagePreviewData["header_type"] = 1;
      } else if (item.format == "IMAGE") {
        templateMessagePreviewData["header_type"] = 2;
        templateMessagePreviewData[
          "url"
        ] = `${ApiRoutes.MEDIA_BASE_URL}${data.url}`;
      } else if (item.format == "VIDEO") {
        templateMessagePreviewData["header_type"] = 3;
        templateMessagePreviewData[
          "url"
        ] = `${ApiRoutes.MEDIA_BASE_URL}${data.url}`;
      } else if (item.format == "DOCUMENT") {
        templateMessagePreviewData["header_type"] = 4;
        templateMessagePreviewData[
          "url"
        ] = `${ApiRoutes.MEDIA_BASE_URL}${data.url}`;
      } else if (item.format == "LOCATION") {
        templateMessagePreviewData["header_type"] = 5;
      }
    } else if (item.type === "BODY") {
      let templateBody = item.text;
      const list = [1, 3, 4, 5, 6, 7].includes(data.template_type)
        ? bodyVariable
        : bodyVariable[0]?.list || []; // template_type = 2 Only For Carousel Template

      if (data?.category == 3) {
        templateBody = `*${item.example.body_text[0]}* is your verification code.`;

        if (item?.add_security_recommendation) {
          templateBody = `${templateBody} For your security, do not share this code.`;
        }
      }
      list.map((variable) => {
        if (variable.variable !== "") {
          templateBody = templateBody?.replace(variable.no, variable.variable);
        }
      });
      templateMessagePreviewData["body"] = templateBody;
    } else if (item.type === "FOOTER") {
      templateMessagePreviewData["footer"] = item.text;
    } else if (item.type === "BUTTONS") {
      templateMessagePreviewData["buttonArray"] = item.buttons.map((button) => {
        if (data.category == 3) {
          button = {
            type: "COPY_CODE",
            text: button?.text,
            copy_code: copyCodeValue[0],
          };
        } else {
          if (button.type == "COPY_CODE") {
            button = { ...button, copy_code: button.example };

            if (data?.template_type == 3) {
              templateMessagePreviewData["simple_code"] = button.example;
            }
          }
        }
        return button;
      });
    } else if (item?.type == "CAROUSEL") {
      item?.cards.map((card, index) => {
        const previewObject = {};

        card.components.map((component) => {
          if (component?.type == "HEADER") {
            const mediaHeaderHandle = component?.example?.header_handle[0];
            templateMessagePreviewData["header_type"] =
              component.format == "IMAGE" ? 2 : 3;
            const mediaObject = data?.carouselMediaList?.find(
              (media) => media.fb_file_token == mediaHeaderHandle
            );
            previewObject[
              "media_url"
            ] = `${ApiRoutes.MEDIA_BASE_URL}${mediaObject?.url}`;
          } else if (component?.type == "BODY") {
            let carouselBody = component.text;
            const variableList = carouselVariable.find(
              (variable) => variable.index == index
            );
            variableList.list.map((variable) => {
              if (variable.variable !== "") {
                carouselBody = carouselBody?.replace(
                  variable.no,
                  variable.variable
                );
              }
            });
            previewObject["body"] = carouselBody;
          } else if (component?.type == "BUTTONS") {
            previewObject["buttons"] = component.buttons;
          }
        });
        previewCarouselCard.push(previewObject);
      });
      templateMessagePreviewData["carousel_card"] = previewCarouselCard;
    } else if (item?.type == "LIMITED_TIME_OFFER") {
      templateMessagePreviewData["limited_time_offer_text"] =
        item?.limited_time_offer?.text;
    }
  });

  return templateMessagePreviewData;
};

export const acceptFileFormat = (type) => {
  return type == 2
    ? "Image Type Allowed JPEG, PNG"
    : type == 3
    ? "Video Type Allowed MP4"
    : "Document Type Allowed PDF";
};

export const acceptFileSize = (type) => {
  return type == 2
    ? "File Size Limit : 5 MB"
    : type == 3
    ? "File Size Limit : 16 MB"
    : " File Size Limit : 100 MB";
};

export const addTextFormatting = (type, textFormatType, bodyText) => {
  const symbol =
    type == "b"
      ? `*${textFormatType}*`
      : type == "i"
      ? `_${textFormatType}_`
      : `~${textFormatType}~`;

  const newBody = bodyText.replace(textFormatType, symbol);

  return newBody;
};

export const getTemplateButtonValue = (type) => {
  return {
    text:
      type == "COPY_CODE"
        ? "Copy Code"
        : type == "URL"
        ? "URL"
        : type == "PHONE_NUMBER"
        ? "Phone Number"
        : type == "CATALOG"
        ? "View catalog"
        : type == "MPM"
        ? "View items"
        : type == "SPM"
        ? "View"
        : type == "FLOW"
        ? "View Form"
        : "",
    type: type,
    ...(type == "URL" && { url: "" }),
    ...(type == "PHONE_NUMBER" && { phone_number: "" }),
    ...(type == "COPY_CODE" && { copy_code: "" }),
  };
};

export const findDuplicateRecord = (arrayOfObj, key) => {
  const grouped = groupBy(arrayOfObj, key);
  const keys = Object.keys(grouped);
  const finalRecordList = [];
  const duplicateRecordList = [];

  keys.forEach((key) => {
    const out = grouped[key].reduce((acc, current) => {
      duplicateRecordList.push(acc);
      return { ...current };
    });
    finalRecordList.push(out);
  });

  return {
    duplicateRecord: duplicateRecordList.length,
    finalRecord: finalRecordList,
  };
};

export const setButtonPosition = (buttonList) => {
  const newButton = [];

  buttonList.map((button) => {
    if (button.type == "COPY_CODE") newButton.unshift(button);
    else newButton.push(button);
  });

  return newButton;
};

export const generateRandomKey = () => {
  // Generates a random 6-character string
  // return Math.random().toString(36).substr(2, 6);
  // Characters to choose from (lowercase letters and underscore)

  const chars = "abcdefghijklmnopqrstuvwxyz_";
  let key = "";
  for (let i = 0; i < 8; i++) {
    key += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return key;
};

export const convertMBtoGB = (value) => {
  if (value == undefined || value == 0) return 0;
  return (value / 1024).toFixed(2);
};

export const getCurrentMonthFirstDate = () => {
  var date = new Date(),
    y = date.getFullYear(),
    m = date.getMonth();
  var firstDay = new Date(y, m, 1);
  return firstDay;
};

export function startDateConvertToUtc(date) {
  return IsNotValue.includes(date)
    ? date
    : localToUtc(new Date(moment(date, "DD-MM-YYYY").format("YYYY-MM-DD")));
}

export function endDateConvertToUtc(date) {
  let newDate = "";
  if (!IsNotValue.includes(date)) {
    newDate = new Date(moment(date, "DD-MM-YYYY").format("YYYY-MM-DD"));
    newDate.setHours(23, 59);
    newDate = localToUtc(newDate);
  }
  return newDate;
}

export const typeWiseOptions = [
  { value: 3, label: "ALL" },
  { value: 0, label: "GST" },
  { value: 1, label: "Non GST" },
  { value: 2, label: "Proforma" },
];

//GET USER WABA DETAILS
export const handleGetWabaDetails = async (metaCheck) => {
  const userData = isUserLoggedIn();
  await getUserWabaDetailsApi({ getPhoneNumbers: true })
    .then((response) => {
      if (response?.status) {
        if (response?.data?.length > 0) {
          secureLocalStorage.setItem("userWabaData", response?.data);
          secureLocalStorage.setItem(
            "userWabaDataCount",
            response?.data[0]?.phoneNumbers?.length || 0
          );
          secureLocalStorage.setItem("currentWabaData", response?.data[0]);
          secureLocalStorage.setItem(
            "currentWabaPhoneNo",
            response?.data[0]?.phoneNumbers[0]
          );
        }
      }
    })
    .catch((error) => showToaster(0, error?.response?.data?.description));
};

export const GradiantIconDivWithTitle = ({
  icon,
  title,
  description,
  iconClass = "gradiant-div",
}) => {
  return (
    <div className="cursor-pointer d-flex align-items-start flex-grow-1 p-0 rounded ">
      <div
        className={`${iconClass} rounded-3 d-flex align-items-center justify-content-center me-75 flex-shrink-0`}
      >
        {icon}
      </div>

      <div className="flex-grow-1 min-width-0">
        <div className="d-flex align-items-center justify-content-between gap-50">
          <h5 className="fw-bolder text-break mb-0">{title}</h5>
        </div>

        <small className="fw-bold mb-0 text-secondary">{description}</small>
      </div>
    </div>
  );
};

export const CounterCardWithIconAndTitle = (item) => {
  return (
    <Card className="rounded-3 shadow-md   h-100">
      <CardBody className="p-1">
        <div className="d-flex align-items-center gap-1">
          {/* Icon */}
          <div
            className={`d-flex align-items-center justify-content-center rounded-3 ${item.iconClass}`}
            style={{ width: "38px", height: "38px" }}
          >
            {item.icon}
          </div>

          {/* Content */}
          <div>
            <span className="fw-bold">{item.title}</span>
            <h3 className="mb-0">{item.count}</h3>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export const BackComponent = ({ backroute = "", className = "" }) => {
  const navigate = useNavigate();
  return (
    <HiOutlineArrowLeft
      className={` ${className} cursor-pointer`}
      size={25}
      title="Back"
      color="grey"
      onClick={() => navigate(`${backroute}`)}
    />
  );
};

export const NavLinkComponentWithIcon = ({
  index,
  item,
  onClickFunction = () => {},
  active,
}) => {
  const Icon = item?.icon;
  return (
    <>
      <NavItem key={index} className="me-2 text-center">
        <NavLink
          className={active == item.value ? "active" : ""}
          onClick={(item) => onClickFunction(item)}
        >
          <Icon color="primary" size="20" />
          <h4
            className={`mb-25 ${
              active === item.value && "text-primary fw-bolder"
            }`}
          >
            {item.label}
          </h4>
        </NavLink>
      </NavItem>
      <h5 className="mb-0 me-2 text-muted d-flex align-items-center text-end">
        |
      </h5>
    </>
  );
};

export const LightBgIconDiv = ({ icon, iconClass = "bg-light-primary" }) => {
  return (
    <div
      style={{ width: "40px", height: "40px" }}
      className={`${iconClass} rounded-3 d-flex align-items-center justify-content-center me-75 flex-shrink-0`}
    >
      {icon}
    </div>
  );
};

const hexToRgba = (hex, alpha = 0.1) => {
  if (!hex) return "";
  const cleanHex = hex.replace("#", "");
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const lightBgLabelBadge = (title, color, index) => {
  return (
    <label
      className="text-center me-50 py-25 px-75 card-badge"
      pill
      key={index}
      style={{
        backgroundColor: hexToRgba(color, 0.1),
        color: color,
        fontSize: "10px",
      }}
      title={title}
    >
      {title}
    </label>
  );
};
